var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Sector, Circle, createText } from '../../element';
import BreadCrumb from '../../widgets/breadcrumb';
import { LegendType } from '../../interface/legend';
import Dataset from '../../core/dataset';
import { SeriesModel } from '../../model';
import Encode from '../../encode';
import { isPercent, getPercentOrNumberValue, degToRad } from '../../utils/math';
import { brighten } from '../../utils/color';
import { flatTree } from '../../utils/tree';
import { getSectorLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
var START_ANGLE = -90;
var END_ANGLE = 270;
var defaultColorEncoder = function (parent) {
    if (parent) {
        return {
            color: brighten(parent.color, 0.08),
        };
    }
};
var SunburstSeriesModel = /** @class */ (function (_super) {
    __extends(SunburstSeriesModel, _super);
    function SunburstSeriesModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'sunburst';
        _this.legendType = LegendType.DATUM_LEGEND;
        return _this;
    }
    SunburstSeriesModel.prototype.getDefaultOption = function () {
        return {
            type: 'sunburst',
            name: '',
            allowDrilldown: true,
            center: ['50%', '50%'],
            size: '75%',
            innerSize: 0,
            shapeStyle: {
                fill: null,
                stroke: '#ffffff',
                lineWidth: 1,
            },
            encode: {
                name: 'name',
                value: 'value',
            },
            breadcrumb: {
                activeColor: '#338AFF',
                textStyle: {
                    color: '#999999',
                    fontSize: 12,
                },
            },
            label: {
                show: false,
                position: 'inside',
                color: null,
                distance: 0,
                fontSize: 12,
                rotate: 'radial',
            },
            connectLine: {
                show: true,
                leadLength: 12,
                lineWidth: 1,
                stroke: '#6E748E',
            },
            level: {},
            hover: {
                shapeStyle: {
                    fill: null,
                    stroke: '#1A1E22',
                    lineWidth: null,
                },
            },
            visible: true,
            showLegend: false,
        };
    };
    SunburstSeriesModel.prototype.getInitialState = function () {
        return {
            points: [],
            cx: 0,
            cy: 0,
            radius: 0,
            innerRadius: 0,
            maxLevel: 0,
            path: [],
            needAnimation: this.needAnimation(),
            tree: [],
        };
    };
    SunburstSeriesModel.prototype.beforeRender = function () {
        var _a = this.option, center = _a.center, size = _a.size, innerSize = _a.innerSize;
        var _b = this.getFacetBBox(), top = _b.top, left = _b.left, width = _b.width, height = _b.height;
        var radius = getPercentOrNumberValue(size, Math.min(width, height)) / 2;
        var innerRadius = getPercentOrNumberValue(innerSize, radius * 2) / 2;
        var cx = (isPercent(center[0]) ? left + getPercentOrNumberValue(center[0], width) : center[0]);
        var cy = (isPercent(center[1]) ? top + getPercentOrNumberValue(center[1], height) : center[1]);
        var rootNode = {
            path: [],
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
            children: [],
            level: 0,
        };
        var points = [];
        var tree = this.getLevelPoints(this._dataset.getData()[0].children, rootNode, points);
        var maxLevel = lodash.max(points.map(function (point) { return point.visible && point.level; }));
        rootNode.children = points;
        this.setState({
            cx: cx,
            cy: cy,
            radius: radius,
            innerRadius: innerRadius,
            points: points,
            maxLevel: maxLevel,
            tree: tree,
        });
    };
    SunburstSeriesModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        _super.prototype.toggleHighlightPoint.call(this, dataIndex, active);
        var shape = this.state.points[dataIndex].shapeRef.current;
        shape.parentNode.add(shape);
        shape.setAttr(__assign(__assign({}, this.getShapeStyle(this.option.shapeStyle)), (active ? this.getShapeStyle(this.option.hover.shapeStyle) : null)));
    };
    SunburstSeriesModel.prototype.isBeforePath = function (path) {
        var current = this.state.path;
        var slicePath = path.slice(0, current.length);
        for (var i = 0; i < slicePath.length; i++) {
            if (slicePath[i] < current[i]) {
                return true;
            }
        }
        return false;
    };
    SunburstSeriesModel.prototype.isAfterPath = function (path) {
        var current = this.state.path;
        var slicePath = path.slice(0, current.length);
        for (var i = 0; i < slicePath.length; i++) {
            if (slicePath[i] > current[i]) {
                return true;
            }
        }
        return false;
    };
    SunburstSeriesModel.prototype.getLevelPoints = function (data, parent, out, children) {
        var _this = this;
        if (children === void 0) { children = []; }
        var _a;
        var isBeforePath = this.isBeforePath(parent.path);
        var isAfterPath = this.isAfterPath(parent.path);
        var isParentRootPath = parent.path.join('.') === this.state.path.join('.');
        var _b = this.option.encode, valueField = _b.value, nameField = _b.name;
        data.sort(function (a, b) { return b[valueField] - a[valueField]; });
        if (isBeforePath) {
            parent.startAngle = parent.endAngle = START_ANGLE;
        }
        if (isAfterPath) {
            parent.startAngle = parent.endAngle = END_ANGLE;
        }
        if (isParentRootPath) {
            parent.startAngle = START_ANGLE;
            parent.endAngle = END_ANGLE;
        }
        var startAngle = parent.startAngle, endAngle = parent.endAngle;
        var dataset = new Dataset(data);
        var valueList = dataset.getFieldData(valueField, true);
        var totalValue = dataset.sum(valueField);
        var delta = endAngle - startAngle;
        var parentPath = parent.path;
        var level = parentPath.length + 1;
        var levelEncodeOption = ((_a = this.option.level[level]) === null || _a === void 0 ? void 0 : _a.encode) || defaultColorEncoder;
        if (lodash.isFunction(levelEncodeOption) && level > 1) {
            levelEncodeOption = levelEncodeOption.call(null, parent);
        }
        var encodeOption = __assign(__assign({}, this.option.encode), levelEncodeOption);
        var encoder = new Encode(encodeOption, this);
        encoder.setDataset(dataset);
        valueList.reduce(function (sliceStartAngle, currentValue, currentIndex) {
            var currentItem = data[currentIndex];
            var encode = encoder.getEncodeData(currentItem, currentIndex);
            var sliceEndAngle = sliceStartAngle + (delta * currentValue) / totalValue;
            var currentPath = parentPath.slice();
            currentPath.push(currentIndex);
            var current = {
                level: parentPath.length + 1,
                path: currentPath,
                data: currentItem,
                name: currentItem[nameField],
                value: currentValue,
                encode: encode,
                color: encode.color,
                startAngle: sliceStartAngle,
                endAngle: sliceEndAngle,
                shapeRef: createRef(),
                visible: !isAfterPath && !isBeforePath,
            };
            current.children = currentItem.children
                ? _this.getLevelPoints(currentItem.children, current, out, [])
                : [];
            out.unshift(current);
            children.push(current);
            return sliceEndAngle;
        }, startAngle);
        return children;
    };
    SunburstSeriesModel.prototype.switchPath = function (path) {
        this.setState({
            path: path,
        });
        this.chart.refresh();
    };
    SunburstSeriesModel.prototype.getPointRadius = function (point) {
        var _a = this.state, radius = _a.radius, innerRadius = _a.innerRadius, maxLevel = _a.maxLevel;
        var barWidth = (radius - innerRadius) / (maxLevel - this.state.path.length);
        var currentLevel = this.state.path.length;
        var level = point.level, visible = point.visible;
        if (!visible) {
            return [innerRadius, innerRadius];
        }
        var levelDelta = level - currentLevel;
        var startRadius = levelDelta > 0 ? innerRadius + barWidth * (levelDelta - 1) : innerRadius;
        var endRadius = levelDelta > 0 ? startRadius + barWidth : startRadius;
        return [startRadius, endRadius];
    };
    SunburstSeriesModel.prototype.renderPoints = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('shapes'),
        });
        var _a = this.state, cx = _a.cx, cy = _a.cy;
        this.state.points.forEach(function (point) {
            var startAngle = point.startAngle, endAngle = point.endAngle, color = point.color, children = point.children;
            var _a = _this.getPointRadius(point), startRadius = _a[0], endRadius = _a[1];
            var shape = new Sector(__assign(__assign({ ref: point.shapeRef, key: point.path.join('.'), cx: cx,
                cy: cy, radius: endRadius, radiusI: startRadius, start: degToRad(startAngle), end: degToRad(endAngle), fill: color }, _this.getShapeStyle(_this.option.shapeStyle)), { cursor: children.length ? 'pointer' : 'auto', onClick: function () { return children.length && _this.option.allowDrilldown && _this.switchPath(point.path); }, onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.wrapPoint(point),
                    });
                } }));
            group.add(shape);
        });
        return group;
    };
    SunburstSeriesModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: 'labels',
        });
        var visiblePoints = flatTree(this.getPointByPath(this.state.path).children);
        var _a = this.state, cx = _a.cx, cy = _a.cy;
        visiblePoints.forEach(function (point) {
            var _a;
            var level = point.level, startAngle = point.startAngle, endAngle = point.endAngle;
            var labelOption = __assign(__assign({}, _this.option.label), (_a = _this.option.level[level]) === null || _a === void 0 ? void 0 : _a.label);
            if (!labelOption.show) {
                return;
            }
            var labelStyle = _this.getTextStyle(labelOption);
            var position = labelOption.position, distance = labelOption.distance, rotate = labelOption.rotate;
            var _b = _this.getPointRadius(point), ri = _b[0], r = _b[1];
            var inside = _this.isLabelInside(labelOption);
            var defaultColor = inside ? '#ffffff' : '#6E748E';
            var textPosition = getSectorLabelPosition({
                cx: cx,
                cy: cy,
                radius: r,
                innerRadius: ri,
                startAngle: startAngle,
                endAngle: endAngle,
            }, position, distance, 'radius', rotate);
            group.add(createText(__assign(__assign(__assign(__assign({ key: point.path.join('.'), text: point.name }, textPosition), { fill: defaultColor }), labelStyle), { pointerEvents: 'none' })));
            if (position === 'outside' && _this.option.connectLine.show) {
                // todo connect line
            }
        });
        return group;
    };
    SunburstSeriesModel.prototype.getPointByPath = function (path) {
        var ret = {
            children: this.state.tree,
            name: this.option.name,
            path: [],
        };
        for (var i = 0; i < path.length; i++) {
            var index = path[i];
            if (i === 0) {
                ret = this.state.tree[index];
            }
            else {
                ret = ret.children[index];
            }
        }
        return ret;
    };
    SunburstSeriesModel.prototype.renderBreadcrumb = function () {
        var _this = this;
        var _a = this.option.breadcrumb, activeColor = _a.activeColor, textStyle = _a.textStyle;
        var _b = this.getFacetBBox(), left = _b.left, right = _b.right, bottom = _b.bottom;
        var pathPoints = [this.getPointByPath([])];
        this.state.path.reduce(function (dataPath, currentPath) {
            dataPath.push(currentPath);
            pathPoints.push(_this.getPointByPath(dataPath));
            return dataPath;
        }, []);
        return new BreadCrumb({
            key: this.getKey('breadcrumb'),
            x: (left + right) / 2,
            y: bottom,
            align: 'center',
            verticalAlign: 'bottom',
            activeColor: activeColor,
            textStyle: this.getTextStyle(textStyle),
            items: pathPoints.map(function (item) { return item.name; }),
            onItemClick: function (index) { return _this.switchPath(pathPoints[index].path); },
        });
    };
    SunburstSeriesModel.prototype.render = function () {
        var _this = this;
        var _a = this.state, cx = _a.cx, cy = _a.cy, innerRadius = _a.innerRadius;
        var group = new Group({
            key: this.getKey('sunburst'),
        });
        group.add(new Circle({
            key: 'sunburst-back',
            cx: cx,
            cy: cy,
            radius: innerRadius,
            fill: 'rgba(0,0,0,0)',
            cursor: 'pointer',
            onClick: function () {
                if (_this.state.path.length > 0) {
                    var pathLen = _this.state.path.length;
                    if (_this.option.allowDrilldown) {
                        _this.switchPath(_this.state.path.slice(0, pathLen - 1));
                    }
                }
            },
        }));
        group.add(this.renderPoints());
        if (this.option.breadcrumb && this.state.path.length) {
            group.add(this.renderBreadcrumb());
        }
        group.add(this.renderLabels());
        return group;
    };
    return SunburstSeriesModel;
}(SeriesModel));
export default SunburstSeriesModel;
registerChart('sunburst', SunburstSeriesModel);
